import { Alerts } from "../../../../../utils/Alerts.js";
import { useToggle } from "../../../../../hooks/useToggle.js";
import { DateTime as DateTime$1 } from "../../../../../components/DateTime/index.js";
import { useCurrentUser } from "../../../../AuthWrapper2/hooks/useCurrentUser.js";
import { useRPCContext } from "../../../../../contexts/RPCContext.js";
import { UserName } from "../../../../Users/shared/UserName.js";
import { useLocation } from "../../../../Locations/hooks/useLocation.js";
import { UserAnPCheck } from "../../../../shared/UserAnPCheck.js";
import { SelectUser } from "../../../../Users/shared/SelectUser.js";
import { EventUserAssignmentStatusPicker } from "./EventUserAssignmentStatusPicker.js";
import { useMemo, useState } from "react";
import { Button, ButtonWithActionModal, Constants, Icon, useContainerDimensionsContext } from "@therms/atalaya";
import { IoClose } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
//#region src/modules/Events/shared/EventInteractive/components/EditEventAssignedUsers.tsx
function EditEventAssignedUsers({ event, eventType, refresh }) {
	const [showAddUser, toggleShowAddUser] = useToggle(false);
	const { rpcService } = useRPCContext();
	const [loading, setLoading] = useState(false);
	const currentUser = useCurrentUser();
	const { data } = useLocation({ locationId: event.locationId }, { fire: !!event.locationId });
	const { viewport: { width: viewportWidth } } = useContainerDimensionsContext();
	const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width;
	const handleAddAssignedUser = async (userId) => {
		setLoading(true);
		if (event.assignedUsers.find((assignedUser) => assignedUser.userId === userId)) {
			Alerts.error("User already assigned to this event");
			setLoading(false);
			return;
		}
		await rpcService.Events.AddEventAssignedUser.v1.call({
			eventId: event.id,
			userId
		}).then(() => {
			refresh();
			Alerts.success("Assignment successful");
		}).catch((err) => {
			console.log("Events.AddEventAssignedUser.v1.call() err", err);
			Alerts.error("There was a problem assigning new User", err.message);
		}).finally(() => setLoading(false));
	};
	const handleRemoveAssignedUser = async (userId) => {
		setLoading(true);
		await rpcService.Events.RemoveEventAssignedUser.v1.call({
			eventId: event.id,
			userId
		}).then(() => {
			refresh();
			Alerts.success("Removed assignment");
		}).catch((err) => {
			console.log("Events.RemoveEventAssignedUser.v1.call() err", err);
			Alerts.error("There was a problem removing assignment", err.message);
		}).finally(() => setLoading(false));
	};
	const handleUpdateAssignedUserStatus = async (userId, status) => {
		setLoading(true);
		await rpcService.Events.SetEventAssignedUserStatus.v1.call({
			eventId: event.id,
			status,
			userId
		}).then(() => refresh()).catch((err) => {
			console.log("Events.SetEventAssignedUserStatus.v1.call() err", err);
			Alerts.error("There was a problem setting assigned User status", err.message);
		}).finally(() => setLoading(false));
	};
	const handleUserAcknowledgement = async (userId) => {
		setLoading(true);
		await rpcService.Events.SetEventAssignedUserAcknowledged.v1.call({
			eventId: event.id,
			userId
		}).then(() => {
			refresh();
			Alerts.success("Acknowledgement successful");
		}).catch((err) => {
			console.log("Events.SetEventAssignedUserAcknowledged.v1.call() err", err);
			Alerts.error("There was a problem acknowledging", err.message);
		}).finally(() => setLoading(false));
	};
	const options = useMemo(() => {
		return eventType.handlingConfig.statuses.map((status) => ({ ...status }));
	}, [eventType]);
	const isCurrentUserAssigned = useMemo(() => {
		return event.assignedUsers.find((assignedUser) => assignedUser.userId === currentUser.id);
	}, [event]);
	return /* @__PURE__ */ jsxs("div", {
		className: "gap-y-xs flex flex-col",
		children: [event.assignedUsers.map((assignedUser) => /* @__PURE__ */ jsxs("div", {
			className: classnames({
				flex: !isMobileLayout,
				"flex-col": isMobileLayout
			}, "hover:bg-surface-subtle gap-y-xs"),
			children: [/* @__PURE__ */ jsxs("div", {
				className: "w-48",
				children: [/* @__PURE__ */ jsx("span", {
					className: "line-clamp-2",
					children: /* @__PURE__ */ jsx(UserName, { user: assignedUser.user })
				}), assignedUser?.status?.setAtDate && /* @__PURE__ */ jsxs("div", {
					className: "text-color-neutral truncate text-sm",
					children: [/* @__PURE__ */ jsx("div", { children: assignedUser?.status?.title }), /* @__PURE__ */ jsx(DateTime$1, {
						relative: true,
						timestamp: assignedUser?.status?.setAtDate
					})]
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex w-full items-center justify-end",
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex grow",
					children: [/* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => user.anp.role.ops.permissions.createEvents && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.ops.permissions.manageEventAssignments,
						children: /* @__PURE__ */ jsx(ButtonWithActionModal, {
							actionText: "Remove",
							body: "Are you sure you want to remove this assignment?",
							className: "mr-xs",
							title: /* @__PURE__ */ jsxs("span", { children: ["Remove ", /* @__PURE__ */ jsx(UserName, { user: assignedUser.user })] }),
							onAction: () => handleRemoveAssignedUser(assignedUser.userId),
							subtle: true,
							variant: "critical",
							children: /* @__PURE__ */ jsx(Icon, { Icon: IoClose })
						})
					}), /* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => user.anp.role.ops.permissions.createEvents && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.ops.permissions.manageEventAssignments || user.id === assignedUser.userId,
						children: /* @__PURE__ */ jsx("div", {
							className: "grow",
							children: !assignedUser.status ? /* @__PURE__ */ jsx(Button, {
								fullWidth: isMobileLayout,
								disabled: loading,
								onClick: () => handleUserAcknowledgement(assignedUser.userId),
								variant: "main",
								children: "Acknowledge"
							}) : /* @__PURE__ */ jsx(EventUserAssignmentStatusPicker, {
								assignedUser,
								event,
								loading,
								options,
								onUpdateAssignedUserStatus: handleUpdateAssignedUserStatus
							})
						})
					})]
				})
			})]
		}, assignedUser.userId)), /* @__PURE__ */ jsx("div", {
			className: "inline-flex w-full",
			children: /* @__PURE__ */ jsx("div", {
				className: !event.assignedUsers.length ? "space-y-xxs grow" : "space-y-xxs mt-3 grow",
				children: showAddUser ? /* @__PURE__ */ jsx(SelectUser, {
					onBlur: toggleShowAddUser,
					onChangeValue: (userId) => {
						handleAddAssignedUser(userId);
						toggleShowAddUser();
					},
					placeholder: "Select User to Assign",
					showStatus: true,
					usersWithStatusFirst: true,
					showDistanceFromGeo: data?.location?.geo?.coordinates
				}) : /* @__PURE__ */ jsxs(UserAnPCheck, {
					check: (user) => user.anp.role.ops.permissions.createEvents && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.ops.permissions.manageEventAssignments,
					children: [!isCurrentUserAssigned && /* @__PURE__ */ jsxs(Button, {
						fullWidth: isMobileLayout,
						className: "mr-sm space-x-xxs",
						onClick: () => handleAddAssignedUser(currentUser.id),
						variant: "main",
						children: [
							/* @__PURE__ */ jsx(Icon, {
								Icon: IoMdAdd,
								size: "sm"
							}),
							" ",
							/* @__PURE__ */ jsx("span", { children: "Assign Yourself" })
						]
					}), /* @__PURE__ */ jsxs(Button, {
						fullWidth: isMobileLayout,
						className: "space-x-xxs",
						onClick: toggleShowAddUser,
						subtle: true,
						variant: "main",
						children: [
							/* @__PURE__ */ jsx(Icon, {
								Icon: IoMdAdd,
								size: "sm"
							}),
							" ",
							/* @__PURE__ */ jsx("span", { children: "Add User Assignment" })
						]
					})]
				})
			})
		})]
	});
}
//#endregion
export { EditEventAssignedUsers };

//# sourceMappingURL=EditEventAssignedUsers.js.map